import React, { useEffect, useState } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "../../../../v2/shared/ui/avatar";
import { Input } from "../../../../v2/shared/ui/input";
import { Button } from "../../../../v2/shared/ui/button";
import { Badge } from "../../../../v2/shared/ui/badge";
import { updateProfileService, uploadImage } from "../service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Loader from "src/legacy/components/loader";
import { toast } from "sonner";
import useProfile from "../hooks/useProfile";

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  profileImage?: string | undefined;
}

const Profile = () => {
  const [formData, setFormData] = useState<ProfileData>({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    profileImage: undefined,
  });
  const [profileImage, setProfileImage] = useState<string | undefined>(undefined)
  const queryClient = useQueryClient()
  const { profileData, isLoadingProfileData } = useProfile()

  const { mutateAsync: updateProfile, isPending: isUpdatingProfile } = useMutation({
    mutationFn: () => updateProfileService({
      firstName: formData.firstName,
      lastName: formData.lastName,
      profileImage: formData.profileImage
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-data"] })
    }
  })
  const { mutateAsync: uploadProfileImage, isPending: isUploadingProfileImage } = useMutation({
    mutationFn: (data: FormData) => uploadImage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-data"] })
    }
  })

  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!isLoadingProfileData && profileData) {
      setFormData(prev => ({
        ...prev,
        firstName: profileData?.firstName,
        lastName: profileData?.lastName,
        email: profileData?.email,
        role: profileData?.roles[0]?.name,
        profileImage: profileData?.profileImage,
      }))
      setProfileImage(profileData?.profileImage)
      setIsDirty(false)
    }
  }, [profileData, isLoadingProfileData])

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true)
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      const res = await uploadProfileImage(formData)
      setFormData(prev => ({
        ...prev,
        profileImage: res?.link
      }))
      try {
        setProfileImage(res?.link as string)
        setIsDirty(true)
      } catch (error) {
        console.log({ error })
        toast.error('Failed to upload image')
      }
    }
  };

  const handleSaveChanges = async () => {
    try {
      await updateProfile()
      toast.success("Profile updated successfully")
      setIsDirty(false)
    } catch (error) {
      console.log({ error })
    }
  };

  const isDisabled = isUploadingProfileImage || isUpdatingProfile || !isDirty

  if (isLoadingProfileData) return <Loader />

  return (
    <section className="space-y-7 rounded-lg md:w-[604px]">
      {/* Profile Photo Section */}
      <div className="space-y-1">
        <p className="text-sm font-[450] text-gray-30">Profile photo</p>
        <div className="flex items-center gap-4">
          <Avatar size="lg" className="size-10">
            <AvatarImage src={profileImage ?? formData.profileImage as string} />
            <AvatarFallback className="bg-gray-50 text-gray-30">
              {formData.firstName.charAt(0)}
              {formData.lastName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={isUploadingProfileImage}
            className="h-auto text-sm font-[450] px-2 py-[2px] rounded-[8px] border-2 border-gray-60"
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploadingProfileImage ? "Uploading..." : "Upload image"}
          </Button>
        </div>
      </div>

      {/* Name Fields Section */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* First Name */}
        <div className="space-y-1">
          <p className="text-sm font-[450] text-gray-30">First name</p>
          <Input
            value={formData.firstName}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            placeholder="First name"
            className="newInput !bg-primary-90"
          />
        </div>

        {/* Last Name */}
        <div className="space-y-1">
          <p className="text-sm font-[450] text-gray-30">Last name</p>
          <Input
            value={formData.lastName}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
            placeholder="Last name"
            className="newInput !bg-primary-90"
          />
        </div>
      </div>

      {/* Email Field */}
      <div className="space-y-1">
        <p className="text-sm font-[450] text-gray-30">Email address</p>
        <Input
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          placeholder="Email address"
          className="newInput !bg-primary-90 opacity-65"
          disabled={true}
        />
      </div>

      {/* Role Section */}
      <div className="space-y-1">
        <p className="text-sm font-[450] text-gray-30">Role</p>
        <Badge className="rounded-[6px] px-2 bg-primary-90 font-[350] py-[2px] h-auto">
          {formData.role}
        </Badge>
      </div>

      {/* Save Changes Button */}
      <Button
        variant="primary"
        size="lg"
        onClick={handleSaveChanges}
        disabled={isDisabled}
        className="w-fit h-10 bg-primary-20 hover:bg-primary-30 text-base font-[450] rounded-[8px] px-4 py-2"
      >
        {isUpdatingProfile ? "Saving..." : "Save changes"}
      </Button>
    </section>
  );
};

export default Profile;
